/**
 * BOM และ Routing — รูปร่างข้อมูลตาม payload contract ที่ freeze แล้ว
 * (`docs/API_CONTRACT.md` — "BOM & Routing — Payload Contract")
 *
 * ไฟล์นี้เป็นภาพสะท้อนของเอกสารนั้นเท่านั้น ไม่ใช่ที่ตัดสินใจเพิ่มเติมเอง ฝั่ง API
 * (`WO-P1-M4-001`) กำลังถูกสร้างขนานกันจากเอกสารฉบับเดียวกัน — ถ้าสองฝั่งไม่ตรงกัน
 * ให้แก้เอกสารก่อน ห้ามฝ่ายใดฝ่ายหนึ่งขยับเอง
 *
 * **สิ่งที่กลุ่มนี้ไม่มีโดยเจตนา: ไม่มี `PATCH` และไม่มี `DELETE`** การแก้ BOM คือการ
 * เขียน revision ถัดไป เพราะใบสั่งผลิตที่ release แล้ว snapshot `bom_id` ไว้และต้อง
 * อ่านสูตรที่ตัวเองถูกสร้างมาจากได้ตลอดอายุของ order (`BUSINESS_RULES.md` ข้อ 2)
 * ชนิดข้อมูลในไฟล์นี้จึงไม่มี body ของการแก้ไขให้เขียนได้เลย
 *
 * ⚠️ ตัวเลข `NUMERIC` (`qty_per_unit`, `scrap_factor`, `std_time`, `setup_time`)
 * เป็น **string** ทั้งขาเข้าและขาออก เพราะ `NUMERIC` เก็บใน IEEE-754 double ไม่ครบ
 * ห้ามแปลงผ่าน `Number`/`parseFloat` ไม่ว่าจะเพื่อแสดงผลหรือเพื่อส่งกลับ
 */

/** รหัสโมดูลของกลุ่มนี้ใน RBAC matrix — ค่าเดียวกับที่ contract ใช้กับทุก endpoint */
export const BOM_ROUTING_MODULE = 'BOM_ROUTING';

/* ------------------------------------------------------------- กฎธุรกิจ --- */

/** `422` เมื่อ product ยังไม่ `ACTIVE` — `DRAFT`/`PENDING_APPROVAL` สร้าง BOM ไม่ได้ */
export const PRODUCT_NOT_ACTIVE = 'PRODUCT_NOT_ACTIVE';

/** `422` เมื่อวันที่มีผลไม่ได้อยู่ถัดจาก `valid_from` ของ revision ปัจจุบัน */
export const EFFECTIVE_DATE_NOT_AFTER_CURRENT = 'EFFECTIVE_DATE_NOT_AFTER_CURRENT';

/** `422` เมื่อชิ้นส่วนอ้างถึง product ตัวเอง — loop ที่ MRP จะวนไม่รู้จบใน Phase 2 */
export const COMPONENT_CANNOT_BE_THE_PRODUCT_ITSELF = 'COMPONENT_CANNOT_BE_THE_PRODUCT_ITSELF';

/** `422` เมื่อ work center อยู่คนละ Plant กับ routing ที่กำลังสร้าง */
export const WORK_CENTER_NOT_IN_PLANT = 'WORK_CENTER_NOT_IN_PLANT';

/**
 * กฎทั้งสี่ที่ contract เขียนไว้ตรง ๆ — ประกาศเป็นรายการเพื่อให้หน้าจอ เทสต์ และ
 * ข้อความบนจอ อ้างสตริงชุดเดียวกัน ไม่มีวันเลื่อนออกจากกัน
 */
export const BOM_ROUTING_RULES = [
  PRODUCT_NOT_ACTIVE,
  EFFECTIVE_DATE_NOT_AFTER_CURRENT,
  COMPONENT_CANNOT_BE_THE_PRODUCT_ITSELF,
  WORK_CENTER_NOT_IN_PLANT,
] as const;

export type BomRoutingRule = (typeof BOM_ROUTING_RULES)[number];

/* --------------------------------------------------------------- แถวข้อมูล */

/** หนึ่งแถวของ `bom_item` ตามตัวอย่างใน contract — ห้าคีย์ ไม่มีคีย์ที่หก */
export interface BomItem {
  readonly item_no: number;
  readonly component_product_code: string;
  /** `NUMERIC` — string เสมอ ห้ามแปลงเป็น number */
  readonly qty_per_unit: string;
  readonly uom: string;
  /** `NUMERIC` — string เสมอ ห้ามแปลงเป็น number */
  readonly scrap_factor: string;
}

/** หนึ่งแถวของ `routing_operation` — `tool_code` เป็น `null` ได้ตาม contract */
export interface RoutingOperation {
  readonly operation_seq: number;
  readonly work_center_code: string;
  /** ไม่ใช่ทุก operation ใช้ tool — `null` คือค่า ไม่ใช่การละเว้น */
  readonly tool_code: string | null;
  /** `NUMERIC` — string เสมอ */
  readonly std_time: string;
  /** `NUMERIC` — string เสมอ */
  readonly setup_time: string;
}

/* ----------------------------------------------------------- revision ----- */

/**
 * หัวของ revision หนึ่งใบ (`bom_header`/`routing_header`) พร้อมแถวทั้งชุด
 *
 * ⚠️ contract freeze **body ของ `POST`** ไว้ครบ แต่ไม่ได้ freeze รูปร่างของคำตอบ
 * `GET` ชนิดข้อมูลนี้จึงบังคับเฉพาะสิ่งที่หน้าจอต้องมีจริงเพื่อทำงานตามกติกา:
 * `revision` (เซิร์ฟเวอร์เป็นผู้กำหนด) และ `valid_from` (ตั้งแต่เมื่อไรถึงมีผล)
 * ส่วน `status`/`valid_to`/`plant_code`/`formula_id` อ่านได้แต่ไม่บังคับ —
 * เดาให้แคบกว่าที่เอกสารเขียนไว้ แล้วปฏิเสธคำตอบที่ถูกต้อง คือความเสียหายที่
 * แย่กว่าการอ่านได้หลายรูปแบบ เรื่องนี้ถูกรายงานกลับเป็น blocker แล้ว
 */
export interface FormulaRevision<TRow> {
  /** `bom_id`/`routing_id` — `null` เมื่อเซิร์ฟเวอร์ไม่ได้ส่งมา */
  readonly formula_id: string | null;
  /** **เซิร์ฟเวอร์เป็นผู้กำหนด** client ส่งมาไม่ได้และผู้ใช้พิมพ์เองไม่ได้ */
  readonly revision: number;
  /** `ACTIVE`/`OBSOLETE`/`DRAFT` — เก็บเป็น string เพื่อให้ค่าที่ยังไม่รู้จักแสดงได้ */
  readonly status: string | null;
  readonly valid_from: string;
  /** `null` = ยังไม่ถูกปิด ซึ่งคู่กับ `status = ACTIVE` คือ revision ที่ใช้อยู่จริง */
  readonly valid_to: string | null;
  readonly plant_code: string | null;
  readonly rows: readonly TRow[];
}

export type BomRevision = FormulaRevision<BomItem>;
export type RoutingRevision = FormulaRevision<RoutingOperation>;

/**
 * คำตอบของ `GET .../bom` และ `GET .../routing`
 *
 * "ยังไม่ได้สร้าง BOM" ตอบ `200` **ไม่ใช่ `404`** และต้องแยกจาก "ไม่มี product นี้"
 * ให้ขาด (contract) — ชนิดข้อมูลจึงมีสถานะ `absent` ของตัวเอง ไม่ใช่ `null` ที่
 * หน้าจอต้องเดาความหมายเอง
 */
export type CurrentFormula<TRow> =
  | { readonly present: false }
  | { readonly present: true; readonly revision: FormulaRevision<TRow> };

/**
 * Body ของ `POST .../bom` และ `POST .../routing`
 *
 * **ไม่มี `revision`** โดยชนิดข้อมูล เพราะเลข revision ที่ client เลือกเองคือการ
 * แข่งกันเขียนทับ `UNIQUE(product_id, plant_id, revision)` (contract)
 *
 * `rows` คือ **ชุดเต็มของ revision ใหม่ ไม่ใช่ delta** — หนึ่งคำขอ = หนึ่ง revision
 * ที่สมบูรณ์ และชุดว่างถูกปฏิเสธก่อนส่ง เพราะ BOM ที่ไม่มีชิ้นส่วนคือ BOM ที่ยังไม่ได้เขียน
 */
export interface CreateFormulaBody<TRow> {
  /** บังคับเสมอ และเป็นขอบเขตสิทธิ์ (D-004) — นอก scope เซิร์ฟเวอร์ตอบ `403` */
  readonly plant_code: string;
  readonly effective_date: string;
  readonly rows: readonly TRow[];
}

/* ------------------------------------------------------- field spec ------- */

/**
 * ชนิดของช่องกรอกหนึ่งช่อง — ใช้ทั้งตอนแสดงตารางและตอนกรอกฟอร์ม
 *
 * `decimal` ไม่เคยผ่าน `Number` ทั้งขาเข้าและขาออก ส่วน `integer` แปลงเป็น number
 * ได้เพราะ `item_no`/`operation_seq` เป็น `INT` ไม่ใช่ `NUMERIC`
 */
export type FormulaFieldKind = 'integer' | 'decimal' | 'code' | 'optional-code';

export interface FormulaFieldSpec {
  readonly key: string;
  readonly label: string;
  readonly kind: FormulaFieldKind;
  readonly hint: string;
}

export type RowValues = Readonly<Record<string, string>>;

export type RowBuildResult<TRow> =
  | { readonly ok: true; readonly row: TRow }
  | { readonly ok: false; readonly problems: readonly string[] };

/**
 * ทุกอย่างที่ทำให้ BOM กับ Routing ใช้หน้าจอและ client ตัวเดียวกันได้
 *
 * สองกลุ่มนี้มีกติกาเดียวกันทุกข้อตาม contract ("กติกาเดียวกับ BOM ทุกข้อ") ต่างกัน
 * แค่ชื่อ path และหน้าตาของแถว การเขียนหน้าจอสองชุดที่เหมือนกัน 90% คือวิธีที่กติกา
 * ข้อหนึ่งจะถูกแก้ที่เดียวแล้วอีกที่ค้างไว้เงียบ ๆ
 */
export interface FormulaSpec<TRow> {
  readonly kind: 'bom' | 'routing';
  /** path segment จริงของ endpoint (`.../bom`) — ไม่มีตารางแปลงชื่อที่เลื่อนจาก URL ได้ */
  readonly pathSegment: string;
  /** คีย์ของ id ในคำตอบ (`bom_id`/`routing_id`) */
  readonly idKey: string;
  /** คีย์ของแถวใน payload (`items`/`operations`) — ชื่อที่ contract เขียนไว้ */
  readonly rowsKey: string;
  readonly title: string;
  readonly rowNoun: string;
  readonly fields: readonly FormulaFieldSpec[];
  readonly blankRow: () => RowValues;
  readonly parseRow: (raw: unknown) => TRow | null;
  readonly serializeRow: (row: TRow) => Record<string, unknown>;
  readonly cell: (row: TRow, key: string) => string;
  readonly toValues: (row: TRow) => RowValues;
  /** คีย์ที่ห้ามซ้ำกันภายในคำขอเดียว (`item_no`/`operation_seq`) */
  readonly rowKey: (row: TRow) => string;
  readonly rowKeyLabel: string;
  readonly buildRow: (values: RowValues, position: number) => RowBuildResult<TRow>;
}

/* --------------------------------------------------------- ตัวช่วยตรวจค่า -- */

/** จำนวนเต็มบวกล้วน — ไม่มีเครื่องหมาย ไม่มีจุดทศนิยม */
const INTEGER_PATTERN = /^\d+$/;

/**
 * ทศนิยมไม่ติดลบในรูปแบบที่ `NUMERIC` รับ — ตรวจด้วย **รูปแบบของสตริง** ไม่ใช่
 * ด้วยการแปลงเป็นตัวเลข เพราะการแปลงคือจุดที่ `NUMERIC(18,4)` เสียความละเอียด
 */
const DECIMAL_PATTERN = /^\d+(\.\d+)?$/;

export function isNonNegativeDecimal(value: string): boolean {
  return DECIMAL_PATTERN.test(value);
}

/**
 * มากกว่าศูนย์จริง — "มีเลขที่ไม่ใช่ 0 อย่างน้อยหนึ่งตัว" ตัดสินได้จากสตริงตรง ๆ
 * (`0.0000` เป็นเท็จ, `0.0001` เป็นจริง) โดยไม่ต้องแตะ `Number` เลย
 */
export function isPositiveDecimal(value: string): boolean {
  return DECIMAL_PATTERN.test(value) && /[1-9]/.test(value);
}

export function isPositiveInteger(value: string): boolean {
  return INTEGER_PATTERN.test(value) && /[1-9]/.test(value);
}

function requiredString(value: unknown): string | null {
  return typeof value === 'string' && value.trim() !== '' ? value : null;
}

function nullableString(value: unknown): string | null | undefined {
  if (value === null || value === undefined) {
    return null;
  }
  return typeof value === 'string' ? value : undefined;
}

function positiveInteger(value: unknown): number | null {
  return typeof value === 'number' && Number.isInteger(value) && value > 0 ? value : null;
}

/* ------------------------------------------------------------- BOM spec --- */

function parseBomItem(raw: unknown): BomItem | null {
  if (typeof raw !== 'object' || raw === null) {
    return null;
  }
  const record = raw as Record<string, unknown>;
  const itemNo = positiveInteger(record.item_no);
  const componentCode = requiredString(record.component_product_code);
  const qty = requiredString(record.qty_per_unit);
  const uom = requiredString(record.uom);
  const scrap = requiredString(record.scrap_factor);
  if (
    itemNo === null ||
    componentCode === null ||
    qty === null ||
    uom === null ||
    scrap === null
  ) {
    return null;
  }
  return {
    item_no: itemNo,
    component_product_code: componentCode,
    qty_per_unit: qty,
    uom,
    scrap_factor: scrap,
  };
}

export const BOM_SPEC: FormulaSpec<BomItem> = {
  kind: 'bom',
  pathSegment: 'bom',
  idKey: 'bom_id',
  rowsKey: 'items',
  title: 'สูตรการผลิต (BOM)',
  rowNoun: 'รายการชิ้นส่วน',
  rowKeyLabel: 'ลำดับรายการ (item_no)',
  fields: [
    {
      key: 'item_no',
      label: 'ลำดับรายการ (item_no)',
      kind: 'integer',
      hint: 'จำนวนเต็มมากกว่า 0 และห้ามซ้ำกันภายในคำขอเดียว',
    },
    {
      key: 'component_product_code',
      label: 'รหัสชิ้นส่วน (component_product_code)',
      kind: 'code',
      hint: 'ต้องมีอยู่จริง และต้องไม่ใช่สินค้าตัวเอง — เซิร์ฟเวอร์เป็นผู้ตรวจทั้งสองข้อ',
    },
    {
      key: 'qty_per_unit',
      label: 'ปริมาณต่อหน่วย (qty_per_unit)',
      kind: 'decimal',
      hint: 'มากกว่า 0 — ส่งเป็นข้อความตามที่พิมพ์ ไม่ปัดเศษ',
    },
    { key: 'uom', label: 'หน่วยนับ (uom)', kind: 'code', hint: 'หน่วยนับของชิ้นส่วนนี้' },
    {
      key: 'scrap_factor',
      label: 'อัตราสูญเสีย (scrap_factor)',
      kind: 'decimal',
      hint: 'ไม่ติดลบ — เป็นค่ามาตรฐานที่คาดไว้ ไม่ใช่ของเสียจริงตอน Scan-Out',
    },
  ],
  blankRow: () => ({
    item_no: '',
    component_product_code: '',
    qty_per_unit: '',
    uom: '',
    scrap_factor: '',
  }),
  parseRow: parseBomItem,
  serializeRow: (row) => ({
    item_no: row.item_no,
    component_product_code: row.component_product_code,
    qty_per_unit: row.qty_per_unit,
    uom: row.uom,
    scrap_factor: row.scrap_factor,
  }),
  cell: (row, key) => {
    switch (key) {
      case 'item_no':
        return String(row.item_no);
      case 'component_product_code':
        return row.component_product_code;
      case 'qty_per_unit':
        return row.qty_per_unit;
      case 'uom':
        return row.uom;
      default:
        return row.scrap_factor;
    }
  },
  toValues: (row) => ({
    item_no: String(row.item_no),
    component_product_code: row.component_product_code,
    qty_per_unit: row.qty_per_unit,
    uom: row.uom,
    scrap_factor: row.scrap_factor,
  }),
  rowKey: (row) => String(row.item_no),
  buildRow: (values, position) => {
    const problems: string[] = [];
    const itemNo = (values.item_no ?? '').trim();
    const component = (values.component_product_code ?? '').trim();
    const uom = (values.uom ?? '').trim();
    // ⚠️ ไม่ trim ค่า NUMERIC: สิ่งที่พิมพ์ต้องถึงเซิร์ฟเวอร์เหมือนเดิมทุกตัวอักษร
    const qty = values.qty_per_unit ?? '';
    const scrap = values.scrap_factor ?? '';

    if (!isPositiveInteger(itemNo)) {
      problems.push(`แถวที่ ${position}: ลำดับรายการต้องเป็นจำนวนเต็มมากกว่า 0`);
    }
    if (component === '') {
      problems.push(`แถวที่ ${position}: ต้องระบุรหัสชิ้นส่วน`);
    }
    if (!isPositiveDecimal(qty)) {
      problems.push(`แถวที่ ${position}: ปริมาณต่อหน่วยต้องเป็นตัวเลขมากกว่า 0`);
    }
    if (uom === '') {
      problems.push(`แถวที่ ${position}: ต้องระบุหน่วยนับ`);
    }
    if (!isNonNegativeDecimal(scrap)) {
      problems.push(`แถวที่ ${position}: อัตราสูญเสียต้องเป็นตัวเลขไม่ติดลบ`);
    }
    if (problems.length > 0) {
      return { ok: false, problems };
    }
    return {
      ok: true,
      row: {
        item_no: Number(itemNo),
        component_product_code: component,
        qty_per_unit: qty,
        uom,
        scrap_factor: scrap,
      },
    };
  },
};

/* --------------------------------------------------------- Routing spec --- */

function parseRoutingOperation(raw: unknown): RoutingOperation | null {
  if (typeof raw !== 'object' || raw === null) {
    return null;
  }
  const record = raw as Record<string, unknown>;
  const seq = positiveInteger(record.operation_seq);
  const workCenter = requiredString(record.work_center_code);
  const std = requiredString(record.std_time);
  const setup = requiredString(record.setup_time);
  const tool = nullableString(record.tool_code);
  if (seq === null || workCenter === null || std === null || setup === null || tool === undefined) {
    return null;
  }
  return {
    operation_seq: seq,
    work_center_code: workCenter,
    tool_code: tool,
    std_time: std,
    setup_time: setup,
  };
}

export const ROUTING_SPEC: FormulaSpec<RoutingOperation> = {
  kind: 'routing',
  pathSegment: 'routing',
  idKey: 'routing_id',
  rowsKey: 'operations',
  title: 'ขั้นตอนการผลิต (Routing)',
  rowNoun: 'ขั้นตอนการผลิต',
  rowKeyLabel: 'ลำดับขั้นตอน (operation_seq)',
  fields: [
    {
      key: 'operation_seq',
      label: 'ลำดับขั้นตอน (operation_seq)',
      kind: 'integer',
      hint: 'จำนวนเต็มมากกว่า 0 และห้ามซ้ำกันภายในคำขอเดียว — เซิร์ฟเวอร์เรียงตามค่านี้เสมอ',
    },
    {
      key: 'work_center_code',
      label: 'หน่วยผลิต (work_center_code)',
      kind: 'code',
      hint: 'ต้องอยู่ใน Plant เดียวกับ Routing นี้ — ต่าง Plant เซิร์ฟเวอร์ตอบ 422',
    },
    {
      key: 'tool_code',
      label: 'เครื่องมือ (tool_code)',
      kind: 'optional-code',
      hint: 'เว้นว่างได้ — ไม่ใช่ทุกขั้นตอนใช้เครื่องมือ',
    },
    {
      key: 'std_time',
      label: 'เวลามาตรฐาน (std_time)',
      kind: 'decimal',
      hint: 'มากกว่า 0 — ส่งเป็นข้อความตามที่พิมพ์ ไม่ปัดเศษ',
    },
    {
      key: 'setup_time',
      label: 'เวลาตั้งเครื่อง (setup_time)',
      kind: 'decimal',
      hint: 'ไม่ติดลบ',
    },
  ],
  blankRow: () => ({
    operation_seq: '',
    work_center_code: '',
    tool_code: '',
    std_time: '',
    setup_time: '',
  }),
  parseRow: parseRoutingOperation,
  serializeRow: (row) => ({
    operation_seq: row.operation_seq,
    work_center_code: row.work_center_code,
    tool_code: row.tool_code,
    std_time: row.std_time,
    setup_time: row.setup_time,
  }),
  cell: (row, key) => {
    switch (key) {
      case 'operation_seq':
        return String(row.operation_seq);
      case 'work_center_code':
        return row.work_center_code;
      case 'tool_code':
        return row.tool_code ?? '';
      case 'std_time':
        return row.std_time;
      default:
        return row.setup_time;
    }
  },
  toValues: (row) => ({
    operation_seq: String(row.operation_seq),
    work_center_code: row.work_center_code,
    tool_code: row.tool_code ?? '',
    std_time: row.std_time,
    setup_time: row.setup_time,
  }),
  rowKey: (row) => String(row.operation_seq),
  buildRow: (values, position) => {
    const problems: string[] = [];
    const seq = (values.operation_seq ?? '').trim();
    const workCenter = (values.work_center_code ?? '').trim();
    const tool = (values.tool_code ?? '').trim();
    const std = values.std_time ?? '';
    const setup = values.setup_time ?? '';

    if (!isPositiveInteger(seq)) {
      problems.push(`แถวที่ ${position}: ลำดับขั้นตอนต้องเป็นจำนวนเต็มมากกว่า 0`);
    }
    if (workCenter === '') {
      problems.push(`แถวที่ ${position}: ต้องระบุหน่วยผลิต`);
    }
    if (!isPositiveDecimal(std)) {
      problems.push(`แถวที่ ${position}: เวลามาตรฐานต้องเป็นตัวเลขมากกว่า 0`);
    }
    if (!isNonNegativeDecimal(setup)) {
      problems.push(`แถวที่ ${position}: เวลาตั้งเครื่องต้องเป็นตัวเลขไม่ติดลบ`);
    }
    if (problems.length > 0) {
      return { ok: false, problems };
    }
    return {
      ok: true,
      row: {
        operation_seq: Number(seq),
        work_center_code: workCenter,
        // ช่องว่าง = ขั้นตอนนี้ไม่ใช้เครื่องมือ ซึ่งคือ `null` ตาม contract
        tool_code: tool === '' ? null : tool,
        std_time: std,
        setup_time: setup,
      },
    };
  },
};
